import { isEqual } from 'lodash'

import {
  getMandatoryChallenges,
  mapChallengesToCoveredDimensions
} from './diagnostic'

/**
 * Draft-Logik des Expertenmodus (career.status === 'WAIT_CONFIG').
 *
 * career_analyse.override_defaults ist die Draft-Schicht der Stelle: angelegt
 * beim Eintritt (confirm-testbattery), verworfen beim Abbrechen, vom Backend
 * (synthesizeAnalyze) in die echte Analyse gefaltet, beim Aktivieren
 * entfernt. Vorher lag dieselbe Logik dreimal in den drei Tabs, jeder Tab
 * parste den kompletten career_analyse-Blob und schrieb ihn zurueck. Genau
 * dadurch sind Render- und Save-Quelle auseinandergelaufen.
 *
 * Bewusst ohne React-Import: so laesst sich die Datei in Jest laden, ohne
 * ueber react-markdown (pure ESM) zu stolpern, das an allen drei Tabs haengt.
 *
 * Draft-Form, identisch zu override_defaults:
 * {
 *   selected_challenges: [{ exam_id }],
 *   dimensions: string[],
 *   selection_difficulty: 'EASY' | 'NORMAL' | 'HARD',
 *   mandatory_criteria: string[]
 * }
 */

export const DEFAULT_SELECTION_DIFFICULTY = 'NORMAL'

/**
 * Dimensionen, die im Muss-Kriterien-Tab nie zur Auswahl stehen. Zwei
 * Culture-Achsen und zwei Roh-Scores ohne eigene Auspraegung. Der Filter lag
 * bisher inline im Fallback von MustCriteriaSelection.
 */
const NON_CRITERION_DIMENSIONS = [
  'HEROS_JOURNEY_score_coopcomp',
  'HEROS_JOURNEY_score_orderchaos',
  'TOWER_OF_LONDON_score',
  'FLANKER_score'
]

/**
 * career_analyse und customer_dashboard_meta sind beides AWSJSON-Strings, die
 * in Altdaten leer, null oder kaputt sein koennen. Ein ungeschuetztes
 * JSON.parse darauf riss ueber die globale ErrorBoundary die ganze Seite mit.
 */
export const parseJsonObject = (value) => {
  try {
    const parsed = JSON.parse(value || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * Dimensionen, die als Muss-Kriterium in Frage kommen: category === 2 und
 * nicht auf der Ausschlussliste.
 */
export const getCriterionDimensions = ({ dimensions, systemDimensions }) =>
  (dimensions || [])
    .filter((key) => !NON_CRITERION_DIMENSIONS.includes(key))
    .filter((key) => systemDimensions?.[key]?.category === 2)

const toExamIds = (selected_challenges) =>
  (selected_challenges || [])
    .map((challenge) => challenge?.exam_id)
    .filter(Boolean)

const toChallenges = (examIds) => examIds.map((exam_id) => ({ exam_id }))

/**
 * Was das Backend-System-Item CHALLENGES nicht kennt, kann die Kandidaten-App
 * nicht spielen, und der Schritt zeigt dafuer auch keine Karte. Dieselbe
 * Absicherung wie in confirm-testbattery und overview, die den Fall (z.B.
 * CULTURE in einer nicht geseedeten Stage) bereits kennen.
 *
 * Ohne Katalog wird nicht gefiltert: gegen eine unbekannte Menge zu filtern
 * wuerde alles verwerfen. Ein leerer Katalog zaehlt dabei als unbekannt, er
 * ist weit eher ein fehlgeschlagener Load des System-Items als die Aussage,
 * dass es keine Challenge gibt.
 */
const onlyAvailable = (examIds, availableExamIds) =>
  availableExamIds?.length
    ? examIds.filter((exam_id) => availableExamIds.includes(exam_id))
    : examIds

/**
 * Fuegt exam_id an der Position ein, die die Konfiguration vorgibt, gemessen
 * an den bereits vorhandenen Ids.
 *
 * Bewusst nicht angehaengt: die Reihenfolge von selected_challenges ist die
 * Spielreihenfolge in der Kandidaten-App. Wuerde eine neue Auswahl hinten
 * landen, haenge die Reihenfolge davon ab, in welcher Reihenfolge jemand
 * geklickt hat, und zwei identisch konfigurierte Stellen wuerden
 * unterschiedlich ausgespielt.
 *
 * Bewusst auch kein Neusortieren der bestehenden Ids: alte Drafts wurden von
 * einer anderen Quelle sortiert (der fruehere Expertenmodus nahm die
 * Reihenfolge aus system.challenges), und die beim blossen Oeffnen des
 * Schritts umzuschreiben waere eine stille Aenderung an einer bestehenden
 * Konfiguration.
 *
 * Eine Id, die die Konfiguration nicht kennt, hat keine definierte Position
 * und geht ans Ende.
 */
const insertByConfigOrder = (examIds, exam_id, configuration) => {
  const configOrder = Object.keys(configuration)
  const rank = configOrder.indexOf(exam_id)

  const index =
    rank === -1
      ? -1
      : examIds.findIndex((id) => {
          const other = configOrder.indexOf(id)
          return other !== -1 && other > rank
        })

  if (index === -1) return [...examIds, exam_id]

  return [...examIds.slice(0, index), exam_id, ...examIds.slice(index)]
}

/**
 * mandatory_criteria muss eine Teilmenge der abgedeckten dimensions sein.
 *
 * Ein Kriterium, dessen Dimension keine ausgewaehlte Challenge mehr misst,
 * ist im Schritt unsichtbar: MustCriteriaSelection rendert aus dimensions,
 * es gibt dafuer also keine Zeile und keinen Schalter. Beim Abschluss faltet
 * synthesizeAnalyze es trotzdem nach scores[*].mandatoryCriterion, und die
 * Zusammenfassung auf Schritt 5 zeigt es an. Der Nutzer saehe dort ein
 * Muss-Kriterium, das ihm nie angeboten wurde und das er nicht entfernen
 * kann.
 *
 * Solche Eintraege entstehen ohne Zutun: confirm-testbattery baut
 * mandatory_criteria aus allen geflaggten Score-Keys, die dimensions dagegen
 * aus der gewaehlten Testbatterie. Dasselbe gilt fuer den Fallback in
 * readAdjustmentDraft, wenn override_defaults fehlt.
 *
 * Leere dimensions zaehlen als unbekannt und filtern nicht, sonst wuerde ein
 * nicht geladenes System-Item die ganze Auswahl verwerfen.
 */
const onlyCovered = (criteria, dimensions) =>
  dimensions?.length
    ? criteria.filter((key) => dimensions.includes(key))
    : criteria

const deriveDimensions = (examIds, systemDimensionsData) =>
  mapChallengesToCoveredDimensions({
    challenges: toChallenges(examIds),
    dimensions: Object.values(systemDimensionsData || {})
  })

/**
 * Baut aus einer Exam-Id-Liste einen vollstaendigen Draft. Einziger Ort, an
 * dem selected_challenges und dimensions gesetzt werden, damit die beiden nie
 * auseinanderlaufen koennen. career_analyse.dimensions leitet das Backend
 * NICHT aus den Challenges ab, fehlt es, bricht das Matching.
 */
const buildDraft = ({
  examIds,
  selection_difficulty,
  mandatory_criteria,
  configuration,
  systemDimensionsData,
  availableExamIds
}) => {
  // Pflicht-Challenges sind nicht abwaehlbar. Sie hier zu erzwingen statt in
  // der UI heisst, dass auch ein krummer Altbestand sie wieder bekommt.
  // Fehlende werden an ihrer Konfigurationsposition eingefuegt, die
  // vorhandene Reihenfolge bleibt unangetastet.
  //
  // Erzwungen wird nur, was der Schritt auch anzeigen kann. Sonst stuende in
  // der Auswahl eine Challenge, fuer die es keine Karte gibt.
  const selectedExamIds = onlyAvailable(
    getMandatoryChallenges(configuration),
    availableExamIds
  ).reduce(
    (ids, exam_id) =>
      ids.includes(exam_id)
        ? ids
        : insertByConfigOrder(ids, exam_id, configuration),
    [...new Set(examIds)]
  )

  const dimensions = deriveDimensions(selectedExamIds, systemDimensionsData)

  return {
    selected_challenges: toChallenges(selectedExamIds),
    dimensions,
    selection_difficulty: selection_difficulty || DEFAULT_SELECTION_DIFFICULTY,
    // Immer ein Array, nie undefined. Das Backend unterscheidet beides:
    // ein leeres Array ist truthy und unterdrueckt damit bewusst den
    // PROBLEM_SOLVING-Default, undefined setzt ihn.
    mandatory_criteria: onlyCovered(
      [...new Set(mandatory_criteria || [])],
      dimensions
    )
  }
}

/**
 * Liest den Draft aus der Stelle. Wirft nie und liefert immer einen
 * vollstaendigen Draft, auch wenn override_defaults fehlt (Stelle direkt in
 * WAIT_CONFIG, Altdaten, abgebrochener Write).
 *
 * Verwaiste Muss-Kriterien fallen dabei weg, siehe onlyCovered in buildDraft.
 * Das ist die eine Stelle, an der ein gespeicherter Wert beim Lesen
 * korrigiert wird. Anders als bei der Reihenfolge oder bei einer unbekannten
 * Challenge geht dabei nichts verloren, was der Nutzer je gesehen oder
 * gesetzt haette: der Schritt zeigt fuer ein verwaistes Kriterium keine
 * Zeile, es ist reiner Geisterzustand.
 *
 * Geschrieben wird deswegen nichts. Der Autosave feuert nur auf eine
 * Nutzeraktion, das Oeffnen allein persistiert also nach wie vor nichts.
 */
export const readAdjustmentDraft = (
  career,
  { configuration, systemDimensionsData, availableExamIds }
) => {
  const career_analyse = parseJsonObject(career?.career_analyse)
  const override_defaults = career_analyse.override_defaults || {}

  const examIds = toExamIds(override_defaults.selected_challenges)

  const mandatory_criteria =
    override_defaults.mandatory_criteria ||
    // Fallback, wenn der Schritt ohne vorheriges confirm-testbattery erreicht
    // wird: dort wird mandatory_criteria aus den Scores vorbelegt, hier muss
    // dieselbe Form entstehen.
    Object.entries(career_analyse.scores || {})
      .filter(([key, value]) => value?.mandatoryCriterion === true)
      .filter(([key]) => !NON_CRITERION_DIMENSIONS.includes(key))
      .map(([key]) => key)

  return buildDraft({
    examIds,
    selection_difficulty:
      override_defaults.selection_difficulty ||
      career_analyse.selection_difficulty,
    mandatory_criteria,
    configuration,
    systemDimensionsData,
    availableExamIds
  })
}

/**
 * Stand beim Betreten des Schritts. Nur fuer das changed-Feld im Tracking,
 * nicht fuer die Anzeige. confirm-testbattery schreibt ihn einmalig nach
 * customer_dashboard_meta, das synthesizeAnalyze nicht anfasst.
 */
export const readAdjustmentBaseline = (
  career,
  { configuration, systemDimensionsData, availableExamIds }
) => {
  const baseline = parseJsonObject(
    career?.customer_dashboard_meta
  ).expert_mode_baseline

  if (!baseline) {
    return readAdjustmentDraft(career, {
      configuration,
      systemDimensionsData,
      availableExamIds
    })
  }

  return buildDraft({
    examIds: toExamIds(baseline.selected_challenges),
    selection_difficulty: baseline.selection_difficulty,
    mandatory_criteria: baseline.mandatory_criteria,
    configuration,
    systemDimensionsData,
    availableExamIds
  })
}

/**
 * Challenge an- oder abwaehlen.
 *
 * Beim Abwaehlen fallen die Muss-Kriterien weg, die keine ausgewaehlte
 * Challenge mehr misst. Ohne das Pruning stuende im Blob ein Kriterium, das
 * der Nutzer im UI nicht mehr sieht, das aber in der Zusammenfassung und im
 * Backend weiterhin als gesetzt gilt.
 *
 * @returns { draft, pruned } pruned = entfernte Dimension-Keys, der Aufrufer
 *          kann sie sich merken und beim Wiederanwaehlen zurueckgeben.
 */
export const toggleChallengeInDraft = (
  draft,
  exam_id,
  {
    configuration,
    systemDimensionsData,
    availableExamIds,
    restoreCriteria = []
  }
) => {
  // Pflicht-Challenges sind nicht schaltbar.
  if (configuration[exam_id]?.standard === true) return { draft, pruned: [] }

  const examIds = toExamIds(draft.selected_challenges)
  const isSelected = examIds.includes(exam_id)

  const nextExamIds = isSelected
    ? examIds.filter((id) => id !== exam_id)
    : insertByConfigOrder(examIds, exam_id, configuration)

  const nextDimensions = deriveDimensions(nextExamIds, systemDimensionsData)

  const wanted = isSelected
    ? draft.mandatory_criteria
    : // Beim Wiederanwaehlen kommen die zuvor automatisch entfernten
      // Kriterien zurueck, damit ein versehentlicher Klick nichts kostet.
      [...draft.mandatory_criteria, ...restoreCriteria]

  // Das Filtern selbst uebernimmt buildDraft, hier wird nur festgehalten,
  // was wegfaellt, damit der Schritt es ansagen kann.
  const pruned = draft.mandatory_criteria.filter(
    (key) => !nextDimensions.includes(key)
  )

  return {
    draft: buildDraft({
      examIds: nextExamIds,
      selection_difficulty: draft.selection_difficulty,
      mandatory_criteria: wanted,
      configuration,
      systemDimensionsData,
      availableExamIds
    }),
    pruned
  }
}

export const setDifficultyInDraft = (draft, difficulty) => ({
  ...draft,
  selection_difficulty: difficulty || DEFAULT_SELECTION_DIFFICULTY
})

export const toggleCriterionInDraft = (draft, dimensionKey) => {
  const { mandatory_criteria } = draft

  return {
    ...draft,
    mandatory_criteria: mandatory_criteria.includes(dimensionKey)
      ? mandatory_criteria.filter((key) => key !== dimensionKey)
      : [...mandatory_criteria, dimensionKey]
  }
}

/**
 * Schreibt den Draft zurueck in den career_analyse-String.
 *
 * Parst bewusst frisch aus dem uebergebenen String statt aus einem
 * Mount-Snapshot und ersetzt ausschliesslich override_defaults. scores,
 * career_preset_id und alle anderen Top-Level-Felder ueberleben damit auch
 * dann, wenn zwischendurch jemand anders geschrieben hat. Career kennt keine
 * Optimistic Concurrency, last write wins.
 */
export const withOverrideDefaults = (career_analyse, draft) =>
  JSON.stringify({
    ...parseJsonObject(career_analyse),
    override_defaults: draft
  })

export const withoutOverrideDefaults = (career_analyse) =>
  JSON.stringify({
    ...parseJsonObject(career_analyse),
    override_defaults: null
  })

/**
 * app_settings.challenges ist das, was die Kandidaten-App tatsaechlich
 * spielt. Pflicht-Challenges fehlen in selected_challenges nur, wenn Render-
 * und Save-Quelle auseinandergelaufen sind. Angehaengt statt einsortiert: im
 * konsistenten Fall ist das Ergebnis identisch (No-op).
 *
 * Gefiltert wird gegen den Katalog, und zwar auch der Draft selbst: eine
 * Challenge, die das System nicht kennt, kann niemand spielen. Sie stehen zu
 * lassen hiesse, dass der Fortschritt eines Talents nie 100 Prozent erreicht,
 * weil spaceControl gegen die Laenge dieser Liste rechnet. Im Draft bleibt
 * sie erhalten, dort ist sie Teil der gespeicherten Konfiguration.
 */
export const buildChallengesForAppSettings = (
  draft,
  { configuration, availableExamIds }
) => {
  const examIds = onlyAvailable(
    toExamIds(draft.selected_challenges),
    availableExamIds
  )

  const missingMandatory = onlyAvailable(
    getMandatoryChallenges(configuration),
    availableExamIds
  ).filter((exam_id) => !examIds.includes(exam_id))

  return toChallenges([...examIds, ...missingMandatory])
}

export const isDraftEqual = (a, b) => isEqual(a, b)
